import { type FC } from 'react';

import {
  CheckboxField,
  CheckboxGroupField,
  SelectField,
  Smokebreak,
} from '@wk/components-react16';

import MultiSelect from '@/shared/ui/MultiSelect';
import { ENVIRONMENTS } from '@/config/constants';
import type { Application } from '@/types';

import type { EvaluationFilters } from '../../hooks/useApplicationEvaluationFilters';

interface ApplicationEvaluationsFiltersProps {
  filters: EvaluationFilters;
  filteredApplications: Application[];
  isMobileViewport: boolean;
  isMobileFiltersOpen: boolean;
  onCloseMobileFilters: () => void;
  onUpdateFilters: (updates: Partial<EvaluationFilters>) => void;
}

const ApplicationEvaluationsFilters: FC<
  ApplicationEvaluationsFiltersProps
> = ({
  filters,
  filteredApplications,
  isMobileViewport,
  isMobileFiltersOpen,
  onCloseMobileFilters,
  onUpdateFilters,
}) => {
  const filtersContent = (
    <>
      <div className="w-full sm:w-auto">
        <SelectField size="small" label="Division" labelFor="division">
          <select
            name="division"
            className="w-full sm:min-w-2xs"
            value={filters.division}
            onChange={(event) => {
              onUpdateFilters({
                division: event.target.value,
                applications: [],
              });
            }}
          >
            <option value="ALL">ALL</option>
            <option value="TAA">TAA</option>
            <option value="HLT">HLT</option>
            <option value="FCC">FCC</option>
            <option value="CORP">CORP</option>
            <option value="CPESG">CPESG</option>
            <option value="DXG">DXG</option>
            <option value="LNR">LNR</option>
            <option value="GBS">GBS</option>
            <option value="GGM">GGM</option>
            <option value="UNKNOWN">UNKNOWN</option>
          </select>
        </SelectField>
      </div>

      <div className="cg-field flex w-full flex-col sm:w-auto">
        <label className="cg-field-label" htmlFor="applications">
          Applications
        </label>
        <MultiSelect
          size="small"
          options={filteredApplications.map((application) => ({
            label: application.name,
            value: application.bitId,
            render: (option) => (
              <span className="flex w-full flex-col gap-1" key={option.value}>
                <span className="w-full truncate text-sm font-medium">
                  {option.label}
                </span>
                <span className="text-xs text-muted-foreground">
                  {option.value}
                </span>
              </span>
            ),
          }))}
          value={filters.applications}
          onChange={(value) => {
            onUpdateFilters({ applications: value });
          }}
          placeholder="Select by Bit ID or Name"
          itemHeight={48}
        />
      </div>

      <div className="cg-field flex w-full flex-col sm:w-auto">
        <label className="cg-field-label" htmlFor="applications">
          Environments
        </label>
        <MultiSelect
          size="small"
          options={ENVIRONMENTS.map((environment) => ({
            label: environment,
            value: environment,
          }))}
          value={filters.environments}
          onChange={(value) => {
            onUpdateFilters({ environments: value });
          }}
          placeholder="Select Environments"
        />
      </div>

      <div className="flex w-full flex-col justify-end gap-5 sm:h-16 sm:w-auto">
        <CheckboxGroupField size="small">
          <div className="flex flex-col gap-2">
            <CheckboxField label="Show IPM Managed Apps Only?">
              <input
                name="ipmOnly"
                type="checkbox"
                checked={filters.ipmOnly}
                onChange={(event) =>
                  onUpdateFilters({ ipmOnly: event.target.checked })
                }
              />
            </CheckboxField>
            <CheckboxField label="Show Evaluated Apps Only?">
              <input
                name="evaluatedOnly"
                type="checkbox"
                checked={filters.evaluatedOnly}
                onChange={(event) =>
                  onUpdateFilters({ evaluatedOnly: event.target.checked })
                }
              />
            </CheckboxField>
          </div>
        </CheckboxGroupField>
      </div>

      <div className="flex w-full flex-col justify-end gap-4 sm:h-16 sm:w-auto">
        <CheckboxGroupField size="small">
          <CheckboxField label="Show Active Apps Only?">
            <input
              name="activeOnly"
              type="checkbox"
              checked={filters.activeOnly}
              onChange={(event) =>
                onUpdateFilters({ activeOnly: event.target.checked })
              }
            />
          </CheckboxField>
        </CheckboxGroupField>
      </div>
    </>
  );

  if (isMobileViewport) {
    return (
      <Smokebreak
        isOpen={isMobileFiltersOpen}
        position="right"
        toFocus='[data-e2e="cg-smokebreak-close"]'
        onUserRequest={onCloseMobileFilters}
      >
        <h2 slot="smokebreakHeader" className="p-3 text-lg font-semibold">
          Filters
        </h2>
        <div
          id="evaluations-filters-panel-mobile"
          className="p-3"
          slot="smokebreakBody"
        >
          <div className="evaluations-filters evaluations-filters-mobile grid w-full grid-cols-1 gap-3">
            {filtersContent}
          </div>
        </div>
      </Smokebreak>
    );
  }

  return (
    <div
      id="evaluations-filters-panel"
      className="evaluations-filters grid w-full grid-cols-1 gap-3 sm:flex sm:items-end sm:gap-x-4 sm:gap-y-1 sm:overflow-x-auto sm:scrollbar sm:scrollbar-h-1 sm:scrollbar-thumb-[#005b92] sm:scrollbar-track-gray-300 sm:pb-1 sm:[&>div]:shrink-0"
    >
      {filtersContent}
    </div>
  );
};

export default ApplicationEvaluationsFilters;
